import { faCheck, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FieldValues } from 'react-hook-form';

import useFetchFormFieldOptions from '@/hooks/useFetchFormFieldOptions';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import DatePicker from '@/ui/components/Form/Datepicker';
import InputField from '@/ui/components/Form/InputField';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import RadioGroup from '@/ui/components/Form/RadioGroup/RadioGroup';
import RichTextEditor from '@/ui/components/Form/RichTextEditor';
import type { OptionType } from '@/ui/components/Form/Select/types';
import Toggle from '@/ui/components/Form/Toggle';
import Spinner from '@/ui/components/Spinner';

import { FieldTypes, type FieldEditorProps } from './types';

function FieldEditor<T extends FieldValues>({
  field,
  isMutable = true,
  value,
  onChange
}: FieldEditorProps<T>) {
  const { loadingOptions, options } = useFetchFormFieldOptions<T>({ field, value });

  const stringLabel = typeof field.label === 'string' ? field.label : '';
  const label = loadingOptions ? <Spinner /> : stringLabel || field.label;
  const placeholder = field.placeholder || stringLabel;

  const disabled = !isMutable || field.readOnly;
  const id = field.key.toString();

  switch (field.type) {
    case FieldTypes.BOOLEAN:
      return (
        <div className="flex flex-col my-2">
          <Toggle
            disableInput={disabled}
            enabled={value}
            onChange={onChange}
            label={field.label}
            enabledIcon={faCheck}
          />
        </div>
      );
    case FieldTypes.DATE:
      return (
        <DatePicker
          disabled={disabled}
          isClearable
          label={field.label}
          name={field.key}
          onChange={onChange}
          value={value ?? ''}
        />
      );
    case FieldTypes.NUMBER:
    case FieldTypes.TEXT:
      return (
        <InputField
          id={field.key.toString()}
          disabled={disabled}
          label={label}
          placeholder={placeholder}
          onChange={(e) => e && onChange(e.target.value)}
          type={field.type}
          value={value ?? ''}
        />
      );
    case FieldTypes.CURRENCY:
      return (
        <InputField
          id={field.key.toString()}
          disabled={disabled}
          label={label}
          placeholder={placeholder}
          step={0.01}
          onChange={(e) => e && onChange(e.target.value)}
          type={FieldTypes.NUMBER}
          typeHelper="dollars"
          value={value ?? ''}
        />
      );
    case FieldTypes.PERCENTAGE:
      return (
        <InputField
          id={field.key.toString()}
          disabled={disabled}
          label={label}
          placeholder={placeholder}
          onChange={(e) => e && onChange(e.target.value)}
          type={FieldTypes.NUMBER}
          typeHelper="percentage"
          value={value ?? ''}
        />
      );
    case FieldTypes.HIDDEN:
      return (
        <input
          id={field.key.toString()}
          disabled={disabled}
          readOnly
          type="hidden"
          value={value ?? ''}
        />
      );
    case FieldTypes.RADIO:
      return (
        <RadioGroup
          id={field.key.toString()}
          disabled={disabled}
          label={label}
          options={options as OptionType<string>[]}
          selectedValue={value}
          onChange={onChange}
        />
      );
    case FieldTypes.SELECT:
      return (
        <AutocompleteSingleSelect
          disabled={disabled}
          formatOptionLabel={field.formatOptionLabel}
          label={
            <div className="flex items-center">
              {label}
              {field.isLoadingOptions && <Spinner className="ml-2" size={4} />}
            </div>
          }
          onChange={(e) => onChange(e?.value ?? null)}
          onSearch={field.onSearch}
          options={options}
          placeholder={field.placeholder ? field.placeholder : 'Select ' + stringLabel + '... '}
          selectedOption={(options.find((option) => option.value === value) as OptionType) ?? null}
        />
      );
    case FieldTypes.TEXTAREA:
      return (
        <>
          <label className="block text-sm font-medium text-gray-700" htmlFor={field.key.toString()}>
            {label}
          </label>
          <textarea
            className="block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            disabled={disabled}
            id={field.key.toString()}
            onChange={(e) => e && onChange(e.target.value)}
            placeholder={placeholder}
            value={value ?? ''}
          />
        </>
      );
    case FieldTypes.RICH_TEXT:
      return (
        <InputGroup>
          <Label htmlFor={id} text={field.label}>
            <RichTextEditor disabled={disabled} onChange={onChange} value={value} />
          </Label>
        </InputGroup>
      );
    case FieldTypes.FILE:
      return (
        <label
          className={`${disabled ? 'cursor-not-allowed bg-gray-200 opacity-50' : 'cursor-pointer'} btn h-48 w-full max-w-72 inline-flex items-center justify-center shadow-md border border-dashed border-gray-400 focus-within:border-indigo-500 hover:bg-gray-100`}
          htmlFor={id}
          style={{
            backgroundSize: 'cover',
            backgroundImage: value ? `url(${URL.createObjectURL(value)})` : 'transparent'
          }}>
          <input
            accept="image/*,application/pdf"
            className="sr-only"
            disabled={disabled}
            id={id}
            onChange={(e) => e && onChange(e.target.files ? e.target.files[0] : null)}
            type="file"
          />
          <span className="p-2 bg-white">
            <FontAwesomeIcon icon={faUpload} className="mr-2" />
            {`${field.label} ${disabled ? '(disabled)' : ''}`}
          </span>
        </label>
      );
    default:
      return null;
  }
}

export default FieldEditor;
